import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeTicketTypeStatus,
  createTicketType,
  deleteTicketType,
  getTicketTypes,
  reorderTicketTypes,
  updateTicketType,
} from '../api/ticketTypeApi'
import type {
  PagedResponse,
  TicketRequestType,
  TicketRequestTypeStatus,
  TicketRequestTypeUpsert,
} from '../types'

const KEY = 'admin-ticket-types'

/**
 * Talep türü yönetimi sunucu-durumu: liste sorgusu + oluştur/güncelle/durum/sil mutation'ları.
 * Her mutation başarısında liste ve bilet lookup'ları (tür seçenekleri) invalidate edilir — değişiklik
 * yeni talep formu/filtresine anında yansır (FR-011). Dil değişince liste yeniden çekilir (TR/EN adlar).
 */
export function useTicketTypes() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
    qc.invalidateQueries({ queryKey: ['ticket-lookups'] })
  }

  // Sunucudan dönen güncel kaydı (yeni modDate ile) listeye hemen yaz — böylece refetch tamamlanmadan
  // yapılan ikinci bir düzenleme/durum değişimi bayat modDate ile 409 (optimistic lock) almaz.
  const applyUpdated = (updated: TicketRequestType) =>
    qc.setQueryData<PagedResponse<TicketRequestType>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getTicketTypes,
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: TicketRequestTypeUpsert) => createTicketType(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TicketRequestTypeUpsert }) =>
      updateTicketType(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: TicketRequestTypeStatus }) =>
      changeTicketTypeStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteTicketType(id),
    onSuccess: invalidate,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderTicketTypes(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<TicketRequestType>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is TicketRequestType => r != null)
        qc.setQueryData<PagedResponse<TicketRequestType>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
